import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  Loader2,
  Clock9,
  AlertCircle,
  Pencil,
  Trash2,
  Building2,
  Hash,
  UserCheck,
  ClipboardList,
  Activity,
  XCircle,
  CheckCircle2,
  Printer
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import styles from './ViewOTAssignment.module.css';
import OTAssignmentDrawer from './OTAssignmentDrawer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import toast from 'react-hot-toast';
import NotFound from '../../../pages/Error/NotFound';
import api from '../../../api/axios';

const MySwal = withReactContent(Swal);

const ViewOTAssignment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved' || s === 'completed') return styles['status-approved'];
    if (s === 'rejected' || s === 'cancelled') return styles['status-rejected'];
    return styles['status-pending'];
  };

  const getStatusLabel = (status) => {
    const s = status?.toLowerCase();
    if (s === 'assigned') return 'Assigned';
    if (s === 'in_progress') return 'In Progress';
    if (s === 'completed') return 'Completed';
    if (s === 'cancelled') return 'Cancelled';
    if (s === 'approved') return 'Approved';
    if (s === 'rejected') return 'Rejected';
    return 'Pending';
  };

  const fetchAssignmentRecord = async () => {
    setLoading(true);
    try {
      const response = await api.get('/overtime/requests', {
        params: {
          per_page: 1000
        }
      });
      if (response.data && response.data.success) {
        const rawList = response.data.data?.data || response.data.data || [];
        const found = rawList.find(item => String(item.id) === String(id));
        
        if (found) {
          const mapped = {
            id: found.id,
            employee: found.employee ? found.employee.employee_name : `Employee ID: ${found.employee_id}`,
            employee_id: found.employee_id,
            employee_code: found.employee ? found.employee.employee_code : `EMP-0${found.employee_id}`,
            department: found.employee?.department ? found.employee.department.name : 'N/A',
            project_site: found.project ? found.project.project_name : 'N/A',
            project_id: found.project_id,
            date: found.ot_date || found.request_date || '-',
            assigned_by: found.assigned_by_user 
              ? (found.assigned_by_user.name || 'Admin') 
              : (found.assigned_by 
                  ? (typeof found.assigned_by === 'object' ? (found.assigned_by.name || 'Admin') : found.assigned_by) 
                  : 'Admin'),
            expected_start_time: found.expected_start_time || found.requested_start_time || '18:00',
            expected_end_time: found.expected_end_time || found.requested_end_time || '22:00',
            instructions: found.remarks || found.remark || found.assignment_reason || found.reason || '-',
            status: found.status || 'assigned'
          };
          setRecord(mapped);
        } else {
          setRecord(null);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to read OT assignment details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignmentRecord();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Cancel Assignment</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Cancel OT Assignment?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to cancel the scheduled OT for <strong>${record?.employee}</strong>?<br/>
          This action cannot be undone.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.post(`/overtime/assignments/${id}/cancel`);
          toast.success('Assignment cancelled successfully');
          navigate('/employees/ot-assignments');
        } catch (error) {
          console.error(error);
          toast.error(error.response?.data?.message || 'Failed to cancel assignment');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={styles['view-ot-layout']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Loading Assignment Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!record) {
    return <NotFound />;
  }

  return (
    <div className={styles['view-ot-layout']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'OT Assignments', path: '/employees/ot-assignments' },
            { label: 'Assignment Details', path: `/employees/ot-assignments/view/${id}` }
          ]} />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>OT Assignment Detail</h1>
              <p>Review summary, schedule details, and instructions for this shift</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Printer size={18} />
                <span>Print</span>
              </button>
              <button className={styles['btn-secondary']} onClick={() => navigate('/employees/ot-assignments')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              {record.status !== 'cancelled' && (
                <>
                  <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                    <Pencil size={18} />
                    <span>Edit Plan</span>
                  </button>
                  <button className={styles['btn-danger']} onClick={handleDelete} title="Cancel Assignment" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '42px', width: '42px', padding: 0 }}>
                    <Trash2 size={18} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['po-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <div className={styles['employee-avatar']} style={{ background: '#eff6ff', color: '#3b82f6', border: '1px solid #dbeafe', width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 800 }}>
                  {record.employee ? record.employee.charAt(0) : 'E'}
                </div>
                <div>
                  <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', margin: 0 }}>{record.employee}</h1>
                </div>
                <span className={`${styles['status-badge']} ${styles[record.status?.toLowerCase()] || ''}`}>
                  {getStatusLabel(record.status).toUpperCase()}
                </span>
              </div>
            </div>

            <div className={styles['po-meta']}>
              <div className={styles['meta-item']}>
                <Hash size={16} />
                <span>Employee Code: <strong>{record.employee_code || `EMP-0${record.employee_id}`}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Building2 size={16} />
                <span>Department: <strong>{record.department || 'Operations'}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <MapPin size={16} />
                <span>Assigned Site: <strong>{record.project_site}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Calendar size={16} />
                <span>Scheduled Date: <strong>{formatDate(record.date)}</strong></span>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-total']}`}><Calendar size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Scheduled Date</label>
                <h2>{formatDate(record.date)}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-paid']}`}><UserCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Assigned By</label>
                <h2>{record.assigned_by}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-tax']}`}><Clock9 size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Scheduled Hours</label>
                <h2>{formatTime(record.expected_start_time)} - {formatTime(record.expected_end_time)}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-balance']}`}><ClipboardList size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Planning Date/Time</label>
                <h2>{record.assignment_time ? record.assignment_time.split(' ')[0] : 'Scheduled'}</h2>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']} style={{ background: '#eff6ff', color: '#3b82f6' }}><ClipboardList size={20} /></div>
                  <h3>Assignment Instructions</h3>
                </div>
                <div className={styles['reason-box']}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Task Description / Notes</label>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6 }}>{record.instructions || record.remark || record.assignment_reason || 'No instructions provided.'}</p>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']} style={{ background: '#f0fdf4', color: '#16a34a' }}><Activity size={20} /></div>
                  <h3>Assignment Metadata</h3>
                </div>
                <div className={styles['compliance-list']}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Creation Date</label>
                    <span style={{ fontWeight: 600, color: '#334155', fontSize: '14px' }}>{record.assignment_time || formatDate(record.date)}</span>
                  </div>
                  <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '16px', marginTop: '16px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Notification Status</label>
                    <span style={{ fontWeight: 600, color: '#10b981', fontSize: '14px' }}>Sent to Employee</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </main>
      </div>

      <OTAssignmentDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
          setIsDrawerOpen(false);
          fetchAssignmentRecord();
        }}
        record={record}
      />
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

const formatTime = (timeString) => {
  if (!timeString) return '—';
  // Check if it is a full ISO date or has a T
  if (timeString.includes('T') || (isNaN(timeString) && !isNaN(Date.parse(timeString)))) {
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch (e) {
      // fallback
    }
  }
  // If it's a "HH:MM:SS" time
  if (timeString.includes(':')) {
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    }
  }
  return timeString;
};

export default ViewOTAssignment;

